import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import UserMenu from './UserMenu';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const MainSidebar = ({ isCollapsed = false, userRole = 'admin' }) => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const { userProfile } = useAuth();
  const [solicitudesNuevas, setSolicitudesNuevas] = useState(0);
  const [accesosPortalPendientes, setAccesosPortalPendientes] = useState(0);
  const [accesosPortalNuevas, setAccesosPortalNuevas] = useState(0);

  // Map new role names to existing ENUM values for backward compatibility
  const normalizeRole = (role) => {
    if (!role) return 'admin';
    const map = {
      'TALLER': 'taller',
      'VENTAS': 'ventas',
      'ADMINISTRACION': 'admin',
      'DIRECCION': 'direccion',
      'RECEPCION': 'recepcion',
    };
    return map?.[role?.toUpperCase()] || role?.toLowerCase();
  };

  const effectiveRole = normalizeRole(userProfile?.role || userRole);

  // Live badge: count unread nueva solicitudes
  useEffect(() => {
    const companyId = userProfile?.company_id;
    if (!companyId) return;

    const fetchCount = async () => {
      const { count } = await supabase
        ?.from('service_requests')
        ?.select('id', { count: 'exact', head: true })
        ?.eq('company_id', companyId)
        ?.eq('status', 'nueva')
        ?.is('read_at', null);
      setSolicitudesNuevas(count || 0);
    };

    fetchCount();

    const channel = supabase
      ?.channel(`sidebar_solicitudes_${companyId}`)
      ?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'service_requests',
        filter: `company_id=eq.${companyId}`,
      }, fetchCount)
      ?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, [userProfile?.company_id]);

  // Live badge: count pending portal access requests (total pendiente + unread nuevas)
  useEffect(() => {
    const companyId = userProfile?.company_id;
    if (!companyId) return;

    const fetchAccesosCount = async () => {
      // Total pendientes
      const { count: totalCount, error: totalError } = await supabase?.from('portal_access_requests')?.select('id', { count: 'exact', head: true })?.eq('status', 'pendiente')?.or(`company_id.eq.${companyId},company_id.is.null`);

      // Nuevas sin leer (read_at IS NULL)
      const { count: nuevasCount, error: nuevasError } = await supabase?.from('portal_access_requests')?.select('id', { count: 'exact', head: true })?.eq('status', 'pendiente')?.is('read_at', null)?.or(`company_id.eq.${companyId},company_id.is.null`);

      if (!totalError) setAccesosPortalPendientes(totalCount || 0);
      if (!nuevasError) setAccesosPortalNuevas(nuevasCount || 0);
    };

    fetchAccesosCount();

    const channel = supabase?.channel(`sidebar_accesos_portal_${companyId}`)?.on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'portal_access_requests',
      }, fetchAccesosCount)?.subscribe();

    return () => { supabase?.removeChannel(channel); };
  }, [userProfile?.company_id]);

  // Mark nuevas as seen when navigating to /accesos-portal
  useEffect(() => {
    if (location?.pathname !== '/accesos-portal') return;
    const companyId = userProfile?.company_id;
    if (!companyId) return;

    const markAsSeen = async () => {
      await supabase?.from('portal_access_requests')?.update({ read_at: new Date()?.toISOString() })?.eq('status', 'pendiente')?.is('read_at', null)?.or(`company_id.eq.${companyId},company_id.is.null`);
      setAccesosPortalNuevas(0);
    };

    markAsSeen();
  }, [location?.pathname, userProfile?.company_id]);

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/workshop-dashboard',
      icon: 'LayoutDashboard',
      roles: ['recepcion', 'taller', 'ventas', 'direccion', 'admin'],
      description: 'Vista general de operaciones del taller'
    },
    {
      label: 'Recepción',
      path: '/vehicle-reception',
      icon: 'ClipboardList',
      roles: ['recepcion', 'taller', 'direccion', 'admin'],
      description: 'Registro de vehículos y órdenes de trabajo'
    },
    {
      label: 'Inspección',
      path: '/technical-inspection',
      icon: 'Search',
      roles: ['taller', 'direccion', 'admin'],
      description: 'Evaluación técnica y documentación'
    },
    {
      label: 'Cotización',
      path: '/quotation-management',
      icon: 'FileText',
      roles: ['ventas', 'direccion', 'admin'],
      description: 'Desarrollo de presupuestos y precios'
    },
    {
      label: 'Ventas',
      path: '/sales-management',
      icon: 'Users',
      roles: ['ventas', 'direccion', 'admin'],
      description: 'Gestión de clientes y coordinación'
    },
    {
      label: 'Proveedores',
      path: '/providers-management',
      icon: 'Building2',
      roles: ['ventas', 'direccion', 'admin'],
      description: 'Gestión de proveedores de refacciones y llantas'
    },
    {
      label: 'Catálogo de Productos',
      path: '/product-catalog',
      icon: 'Package',
      roles: ['ventas', 'direccion', 'admin'],
      description: 'Catálogo maestro de productos, precios y compatibilidad'
    },
    {
      label: 'CRM',
      path: '/crm-management',
      icon: 'UserCheck',
      roles: ['ventas', 'direccion', 'admin'],
      description: 'Pipeline de ventas e interacciones con clientes'
    },
    {
      label: 'Solicitudes',
      path: '/solicitudes-management',
      icon: 'ClipboardList',
      roles: ['admin', 'recepcion', 'direccion', 'ventas'],
      description: 'Solicitudes del Portal Cliente',
      badge: solicitudesNuevas,
    },
    {
      label: 'Red Gearcontrol',
      path: '/red-gearcontrol',
      icon: 'Network',
      roles: ['recepcion', 'ventas', 'taller', 'direccion', 'admin'],
      description: 'Talleres afiliados y compartir historial'
    },
    {
      label: 'Clientes',
      path: '/clients-management',
      icon: 'BookUser',
      roles: ['recepcion', 'ventas', 'direccion', 'admin'],
      description: 'Ficha maestra de clientes y unidades'
    },
    {
      label: 'Accesos Portal',
      path: '/accesos-portal',
      icon: 'ShieldCheck',
      roles: ['admin', 'direccion', 'recepcion'],
      description: 'Solicitudes de acceso al Portal Cliente',
      badge: accesosPortalPendientes,
      badgePulse: accesosPortalNuevas > 0,
    },
    {
      label: 'Historial de Vehículo',
      path: '/vehicle-history',
      icon: 'History',
      roles: ['recepcion', 'ventas', 'taller', 'direccion', 'admin'],
      description: 'Historial completo por unidad'
    },
    {
      label: 'Archivo',
      path: '/vehicle-archive',
      icon: 'FolderOpen',
      roles: ['recepcion', 'ventas', 'taller', 'direccion', 'admin'],
      description: 'Carpeta por cliente/unidad: servicios, garantías, evidencias'
    },
    {
      label: 'Calendario',
      path: '/operational-calendar',
      icon: 'Calendar',
      roles: ['recepcion', 'taller', 'ventas', 'direccion', 'admin'],
      description: 'Agenda operativa de órdenes y entregas'
    },
    {
      label: 'Timeline',
      path: '/work-order-timeline',
      icon: 'Clock',
      roles: ['recepcion', 'ventas', 'direccion', 'admin'],
      description: 'Seguimiento de órdenes de trabajo'
    },
    {
      label: 'Entrega',
      path: '/vehicle-delivery-confirmation',
      icon: 'Truck',
      roles: ['recepcion', 'taller', 'direccion', 'admin'],
      description: 'Confirmación de entrega de vehículos'
    },
    {
      label: 'Feedback',
      path: '/customer-feedback',
      icon: 'MessageSquare',
      roles: ['recepcion', 'ventas', 'direccion', 'admin'],
      description: 'Evaluación de satisfacción del cliente'
    },
    {
      label: 'Auditoría',
      path: '/audit-log',
      icon: 'Shield',
      roles: ['direccion', 'admin'],
      description: 'Registro de auditoría y cambios del sistema'
    },
    {
      label: 'Reportes',
      path: '/advanced-reporting',
      icon: 'BarChart3',
      roles: ['direccion', 'admin'],
      description: 'Reportes avanzados de ingresos y desempeño'
    },
    {
      label: 'Análisis de Clientes',
      path: '/client-analytics',
      icon: 'Users',
      roles: ['ventas', 'direccion', 'admin'],
      description: 'Análisis de clientes y valor de vida'
    },
    {
      label: 'Email',
      path: '/email-management',
      icon: 'Mail',
      roles: ['direccion', 'admin'],
      description: 'Gestión de reportes programados por email'
    },
    {
      label: 'Plantillas Email',
      path: '/email-template-management',
      icon: 'FileText',
      roles: ['direccion', 'admin'],
      description: 'Gestión de plantillas de email por segmento'
    },
    {
      label: 'Automatización',
      path: '/automation-rules-management',
      icon: 'Zap',
      roles: ['direccion', 'admin'],
      description: 'Reglas de automatización de emails'
    },
    {
      label: 'Análisis Auto.',
      path: '/automation-analytics-dashboard',
      icon: 'BarChart',
      roles: ['direccion', 'admin'],
      description: 'Análisis de rendimiento de automatización'
    },
    {
      label: 'Identidad y Papelería',
      path: '/company-branding',
      icon: 'Palette',
      roles: ['admin', 'direccion'],
      description: 'Configura la identidad visual y papelería corporativa'
    },
    {
      label: 'QA de Rutas',
      path: '/qa/routes',
      icon: 'RouteOff',
      roles: ['admin', 'direccion'],
      description: 'Validación de rutas internas de la app'
    }
  ];

  const filteredNavigation = navigationItems?.filter(item =>
    item?.roles?.includes(effectiveRole)
  );

  const toggleMobileMenu = () => {
    setIsMobileOpen(!isMobileOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileOpen(false);
  };

  return (
    <>
      {/* Mobile hamburger button */}
      <button
        onClick={toggleMobileMenu}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-md bg-card shadow-elevation-2 hover:bg-muted transition-smooth press-effect focus-ring"
        aria-label="Toggle navigation menu"
      >
        <Icon name={isMobileOpen ? 'X' : 'Menu'} size={24} />
      </button>

      {/* Mobile backdrop overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full bg-card border-r border-border
          transition-smooth flex flex-col
          ${isCollapsed ? 'w-20' : 'w-64'}
          ${isMobileOpen ? 'translate-x-0 z-50' : '-translate-x-full lg:translate-x-0 z-40'}
        `}
      >
        {/* Logo / Header */}
        <div className={`sidebar-header ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="sidebar-logo">
            <Icon name="Wrench" size={isCollapsed ? 20 : 24} className="sidebar-logo-icon" />
          </div>
          {!isCollapsed && (
            <div className="ml-3 sidebar-logo-text">
              <h1 className="text-lg font-heading font-semibold text-foreground">
                Gearcontrol
              </h1>
              <p className="text-xs caption text-muted-foreground">
                Sistema de Taller
              </p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {filteredNavigation?.map((item) => {
            const isActive = location?.pathname === item?.path;

            return (
              <NavLink
                key={item?.path}
                to={item?.path}
                onClick={closeMobileMenu}
                className={`
                  flex items-center gap-3 px-4 py-2.5 rounded-lg
                  transition-smooth hover-lift press-effect focus-ring
                  ${isActive
                    ? 'bg-primary text-primary-foreground shadow-elevation-1'
                    : 'text-foreground hover:bg-muted'
                  }
                  ${isCollapsed ? 'justify-center' : ''}
                `}
                title={isCollapsed ? item?.label : item?.description}
              >
                <Icon
                  name={item?.icon}
                  size={20}
                  className={isActive ? 'text-primary-foreground' : 'text-muted-foreground'}
                />
                {!isCollapsed && (
                  <span className="font-body font-medium text-sm flex-1">
                    {item?.label}
                  </span>
                )}
                {!isCollapsed && item?.badge > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[11px] font-bold bg-red-500 text-white${item?.badgePulse ? ' animate-pulse' : ''}`}>
                    {item?.badge > 9 ? '9+' : item?.badge}
                  </span>
                )}
                {isCollapsed && item?.badge > 0 && (
                  <span className={`absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500${item?.badgePulse ? ' animate-pulse' : ''}`} />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* User menu at bottom */}
        <div className="p-4 border-t border-border">
          <UserMenu />
        </div>
      </aside>
    </>
  );
};

export default MainSidebar;